import React, { useState, useEffect } from 'react';
import { FormControl, ActionList, ActionMenu } from '@primer/react';
import { CalendarIcon, ChevronLeftIcon } from '@primer/octicons-react';

interface MonthYearInputProps {
    value: string;
    onChange: (value: string) => void;
    type?: 'from' | 'to';
    className?: string;
    disabled?: boolean;
    error?: boolean;
    minDate?: string;
    maxDate?: string;
}

const MONTHS = [
    { value: '01', label: 'Январь' },
    { value: '02', label: 'Февраль' },
    { value: '03', label: 'Март' },
    { value: '04', label: 'Апрель' },
    { value: '05', label: 'Май' },
    { value: '06', label: 'Июнь' },
    { value: '07', label: 'Июль' },
    { value: '08', label: 'Август' },
    { value: '09', label: 'Сентябрь' },
    { value: '10', label: 'Октябрь' },
    { value: '11', label: 'Ноябрь' },
    { value: '12', label: 'Декабрь' },
];

const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 10; i--) {
        years.push(i.toString());
    }
    return years;
};

const MonthYearInput: React.FC<MonthYearInputProps> = ({
    value,
    onChange,
    type,
    className = '',
    disabled = false,
    error,
    minDate,
    maxDate
}) => {
    const [selectedYear, setSelectedYear] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (!value) {
            setSelectedYear(null);
        }
    }, [value]);

    const parseValue = (val: string): { month: string; year: string } => {
        if (!val) return { month: '', year: '' };
        const [year, month] = val.split('-');
        return { month: month || '', year: year || '' };
    };

    const { month, year } = parseValue(value);

    const formatDisplayValue = (): string => {
        if (!month || !year) return '';
        return `${year}-${month}`;
    };

    const getAvailableYears = (): string[] => {
        const allYears = generateYears();

        if (!minDate && !maxDate) return allYears;

        const minYear = minDate ? parseInt(minDate.split('-')[0]) : null;
        const maxYear = maxDate ? parseInt(maxDate.split('-')[0]) : null;

        return allYears.filter(y => {
            const yearNum = parseInt(y);
            if (minYear && yearNum < minYear) return false;
            if (maxYear && yearNum > maxYear) return false;
            return true;
        });
    };

    const getAvailableMonths = (yearStr: string): typeof MONTHS => {
        if (!minDate && !maxDate) return MONTHS;

        const yearNum = parseInt(yearStr);
        const minYear = minDate ? parseInt(minDate.split('-')[0]) : null;
        const minMonth = minDate ? minDate.split('-')[1] : null;
        const maxYear = maxDate ? parseInt(maxDate.split('-')[0]) : null;
        const maxMonth = maxDate ? maxDate.split('-')[1] : null;

        return MONTHS.filter(m => {
            if (minYear && yearNum === minYear && minMonth && m.value < minMonth) return false;
            if (maxYear && yearNum === maxYear && maxMonth && m.value > maxMonth) return false;
            return true;
        });
    };

    const handleYearSelect = (y: string, e: any) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedYear(y);
    };

    const handleMonthSelect = (m: string, e: any) => {
        e.preventDefault();
        e.stopPropagation();
        if (selectedYear) {
            const newValue = `${selectedYear}-${m}-01`;
            onChange(newValue);
            setSelectedYear(null);
            setIsOpen(false);
        }
    };

    const handleBack = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedYear(null);
    };

    const getPlaceholder = (): string => {
        return type === 'from' ? 'От' : 'До';
    };

    return (
        <FormControl required key={`${type}-control`}>
            <FormControl.Label className='invisible h-0'></FormControl.Label>
            <div className={`relative ${className} ${(value ? 'border-none' : 'border border-border')} rounded-md`}>
                <ActionMenu open={isOpen} onOpenChange={setIsOpen}>
                    <ActionMenu.Button
                        leadingVisual={CalendarIcon}
                        disabled={disabled}
                        variant={value ? 'default' : 'invisible'}
                        style={{ height: '40px' }}
                        sx={{
                            px: 3,
                            '& > span': {
                                justifyContent: 'flex-start !important',
                                textAlign: 'left',
                                width: '80%',
                            },
                        }}
                    >
                        <span className="w-full text-left">
                            {value ? formatDisplayValue() : (
                                <span className="text-gray-500">{getPlaceholder()}</span>
                            )}
                        </span>
                    </ActionMenu.Button>
                    <ActionMenu.Overlay>
                        {!selectedYear ? (
                            <ActionList selectionVariant="single">
                                {getAvailableYears().map((y) => (
                                    <ActionList.Item
                                        key={y}
                                        selected={year === y}
                                        onSelect={(e) => {
                                            e.preventDefault();
                                            handleYearSelect(y, e);
                                        }}
                                    >
                                        {y}
                                    </ActionList.Item>
                                ))}
                            </ActionList>
                        ) : (
                            <ActionList selectionVariant="single">
                                <ActionList.Item
                                    onSelect={(e) => {
                                        e.preventDefault();
                                        handleBack(e);
                                    }}
                                >
                                    <ActionList.LeadingVisual>
                                        <ChevronLeftIcon />
                                    </ActionList.LeadingVisual>
                                    {selectedYear}
                                </ActionList.Item>
                                <ActionList.Divider />
                                {getAvailableMonths(selectedYear).map((m) => (
                                    <ActionList.Item
                                        key={m.value}
                                        selected={year === selectedYear && month === m.value}
                                        onSelect={(e) => handleMonthSelect(m.value, e)}
                                    >
                                        {m.label}
                                    </ActionList.Item>
                                ))}
                            </ActionList>
                        )}
                    </ActionMenu.Overlay>
                </ActionMenu>
            </div>
            {error && (
                <FormControl.Validation variant="error" className='invisible h-0'></FormControl.Validation>
            )}
        </FormControl>
    );
};

export { MonthYearInput };